import React, { useState } from 'react';
import {
  AlertBanner,
  ApplicationIcon,
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import { useHistory } from 'react-router-dom';

import { isEmpty, useInvertedLocalize } from '../../../../../utils';
import {
  type Provider,
  type ProviderResponse,
} from '../../../data-layer/types';
import { useUpdateIDPProvider } from '../../../hooks';

import invertedMessages from './messages';

import './Header.css';

interface HeaderProps {
  currentProvider: ProviderResponse | undefined;
  provider: Provider;
  resetBanners: () => void;
}

function Header({ currentProvider, provider, resetBanners }: HeaderProps) {
  const { mutate: updateIDPConfig } = useUpdateIDPProvider();
  const l = useInvertedLocalize(invertedMessages);
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState('');
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);

  const closeHandler = () => {
    history.push('/idproofing/providers', {});
  };

  const successHandler = (action: string, title: string) => {
    history.push('/idproofing/providers', { action, title });
  };

  return (
    <div>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isFloating
        position={{
          type: 'absolute',
          top: '8rem',
          left: '7rem',
        }}
        shouldAutoDismiss
        isColonVisible={false}
      />
      {currentProvider && provider && !isEmpty(currentProvider.config) && (
        <div className='Ext-IDDataWebSettings-header'>
          <Box className='Ext-IDDataWebSettings-header-text'>
            <ButtonIcon
              size='large'
              kind='ghost'
              icon={<Icon kind='ChevronLeft' />}
              onClick={() => {
                resetBanners();
                closeHandler();
              }}
              ariaLabel='Close'
            />
            <ApplicationIcon
              className='Ext-IDDataWebSettings-icon'
              logo={provider.icon}
              size='xLarge'
            />
            <Box className='Ext-IDDataWebSettings-header-flexbox'>
              <Typography kind='h2' color='--color-foreground-neutral-bold'>
                {provider.name}
              </Typography>
              {currentProvider && (
                <Chip
                  size='xSmall'
                  label={currentProvider.status === 1 ? 'Enabled' : 'Disabled'}
                  color={currentProvider.status === 1 ? 'success' : 'neutral'}
                />
              )}
            </Box>
          </Box>
          {currentProvider && provider && (
            <Menu
              className='Ext-IDDataWebSettings-header-menu'
              options={[
                {
                  label: currentProvider.status === 0 ? 'Disable' : 'Enable',
                  value: currentProvider.status === 0 ? 'Disable' : 'Enable',
                },
              ]}
              CustomMenu={
                <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
                  <MenuItem
                    option={{
                      label:
                        currentProvider.status === 0 ? 'Enable' : 'Disable',
                      value:
                        currentProvider.status === 0 ? 'Enable' : 'Disable',
                    }}
                    onClick={() => {
                      if (currentProvider.status === 0) {
                        const providerConfig = currentProvider;

                        providerConfig.status = 1;

                        if (
                          providerConfig.config &&
                          'PASSWORD' in providerConfig.config
                        ) {
                          delete providerConfig.config.PASSWORD;
                        }

                        updateIDPConfig(providerConfig, {
                          onSuccess(response) {
                            if (response.errorMessage) {
                              setErrorMessage(response.errorMessage);
                              setShowCriticalBanner(true);
                            } else {
                              successHandler('Enabled', provider.name);
                            }
                          },
                        });
                      } else {
                        const providerConfig = currentProvider;

                        providerConfig.status = 0;

                        if (
                          providerConfig.config &&
                          'PASSWORD' in providerConfig.config
                        ) {
                          delete providerConfig.config.PASSWORD;
                        }

                        updateIDPConfig(providerConfig, {
                          onSuccess(response) {
                            if (response.errorMessage) {
                              setErrorMessage(response.errorMessage);
                              setShowCriticalBanner(true);
                            } else {
                              successHandler('Disabled', provider.name);
                            }
                          },
                        });
                      }
                    }}
                    tabIndex={0}
                  />
                </ul>
              }
              trigger={<Icon kind='MoreVertical' size='medium' />}
            />
          )}
        </div>
      )}
      {isEmpty(currentProvider) && provider && (
        <div className='Ext-IDDataWebSettings-breadcrumb'>
          <div className='Ext-IDDataWebSettings-breadcrumb-header'>
            <Typography
              kind='body2-bold'
              color='--color-foreground-primary-medium'>
              {l('ID proofing')}
            </Typography>
            <Icon kind='ChevronRight' size='xSmall' />
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {provider.name}
            </Typography>
          </div>
          <div className='Ext-IDDataWebSettings-breadcrumb-provider'>
            <ApplicationIcon
              className='Ext-IDDataWebSettings-icon'
              logo={provider.icon}
              size='xLarge'
            />
            <Typography kind='h1'>{provider.name}</Typography>
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
